// frontend/src/hooks/useZoteroImport.ts
import { useMutation } from "@tanstack/react-query";
import apiClient from "@/lib/api-client";
import { extractDoiFromPdfBlob } from "@/lib/pdfDoiExtractor"; // same util the watcher used
import type {
  ManifestEntry, ManifestResponse, PreviewResponse,
} from "@/types/zoteroImport";
import type { MatchedEntry } from "@/lib/zoteroFileMatch";

/** Fetch the attachment manifest for selected collections (Web API path). */
export function useZoteroManifest() {
  return useMutation({
    mutationFn: (collectionKeys: string[]) =>
      apiClient
        .post("api/zotero-sync/manifest", {
          json: { collection_keys: collectionKeys },
          timeout: 120_000,
        })
        .json<ManifestResponse>(),
  });
}

/** Dedup-check selected entries against the user's existing library. */
export function useZoteroImportPreview() {
  return useMutation({
    mutationFn: (entries: ManifestEntry[]) =>
      apiClient
        .post("api/zotero-sync/preview", {
          json: {
            items: entries.map((e) => ({
              attachment_key: e.attachment_key, doi: e.doi, title: e.title,
            })),
          },
          timeout: 60_000,
        })
        .json<PreviewResponse>(),
  });
}

/** Upload one matched attachment's PDF and attach it to a concept with a role. */
export async function uploadAttachment(
  m: MatchedEntry,
  conceptId: string,
  role: "trawled" | "foundational",
): Promise<void> {
  if (!m.file) throw new Error("no local file");
  const doi = m.entry.doi ?? (await extractDoiFromPdfBlob(m.file).catch(() => null));
  const fd = new FormData();
  fd.append("pdf", m.file, m.file.name);
  fd.append("filename", m.entry.filename ?? m.file.name);
  if (doi) fd.append("doi", doi);
  if (m.entry.title) fd.append("title", m.entry.title);
  fd.append("concept_id", conceptId);
  fd.append("role", role);
  await apiClient.post("api/zotero-sync/import-upload", { body: fd, timeout: 120_000 });
}
